import { useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Form,
  Button,
  Spinner,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { toast } from "../../utils/toast";
import useRoutes from "../../store/routes";
import { addRouteApi, updateRoute } from "../../api/routes";
import { routeErrorText } from "../../utils/routeApiErrors";
import SearchDropdown from "../searchDropdown";
import { 
  Route as RouteIcon, 
  Calendar,
  X,
  User,
  Check
} from "lucide-react";
import './createRouteBeartrack.scss';

export default function CreateRouteBeartrack({
  edit,
  closeModal,
  route,
}) {
  const { t } = useTranslation();
  const deliveries = useRoutes(state => state.deliveries || []);
  const [loading, setLoading] = useState(false);
  const fetchRoutes = useRoutes((state) => state.fetchRoutesBeartrack);
  const [dayType, setDayType] = useState("weekday");

  const methods = useForm({
    mode: "onSubmit",
    defaultValues: edit && route
      ? route
      : {
          frequency: "weekday",
          time: "",
        },
  });

  useEffect(() => {
    if (edit && deliveries.length > 0 && route) {
      // Mapear frecuencia a valores del formulario
      let frequencyValue = "weekday";
      if (route.frequency === "Sábado") {
        frequencyValue = "saturday";
      } else if (route.frequency === "Domingo") {
        frequencyValue = "sunday";
      }

      // Obtener el ID del repartidor de diferentes formas posibles
      const deliveryManId = route.deliveryMan?.id || 
                           route.deliveryMan?._id || 
                           route.idDeliveryMan ||
                           route.deliveryManId;

      const deliveryData = deliveries.find(
        (d) => d._id === deliveryManId
      );

      if (!deliveryData) {
        console.warn("No se encontró el repartidor para la ruta:", route);
        methods.reset({
          routeName: route.routeName || "",
          deliveryMan: null,
          frequency: frequencyValue,
          time: route.time || route.preferredTime || "",
        });
        setDayType(frequencyValue);
        return;
      }

      const deliveryManOption = {
        value: deliveryData._id,
        label: `${deliveryData.name} ${deliveryData.lastName}`,
      };
      
      methods.reset({
        routeName: route.routeName || "",
        deliveryMan: deliveryManOption.value, // Guardamos solo el ID
        frequency: frequencyValue,
        time: route.time || route.preferredTime || "",
      });
      setDayType(frequencyValue);
    } else if (!edit) {
      // Inicializar valores por defecto para crear nueva ruta
      methods.reset({
        routeName: "",
        deliveryMan: null,
        frequency: "weekday",
        time: "",
      });
    }
  }, [edit, route, methods, deliveries]);

  const submitValues = useCallback(
    async (values) => {
      setLoading(true);

      const { deliveryMan, routeName = "", time, ...otherValues } = values;

      // Buscar el label del repartidor seleccionado para construir el nombre de la ruta
      const selectedDelivery = deliveries.find(d => d._id === deliveryMan);
      const deliveryManLabel = selectedDelivery 
        ? `${selectedDelivery.name} ${selectedDelivery.lastName}` 
        : t("Sin repartidor");

      const updatedValues = {
        ...otherValues,
        preferredTime: time,
        deliveryMan: deliveryMan,
        routeName:
          routeName.length === 0
            ? constructRouteName({ ...values, deliveryMan: { label: deliveryManLabel } })
            : routeName,
      };

      const routeId = route?.id || route?._id;

      // addRouteApi/updateRoute never reject: they resolve `{ error }`. The
      // promise below does reject, so toast.promise renders its error branch.
      // Presence, not truthiness: a failed response with an empty body arrives
      // as `error: ''` (axios keeps it as-is) and must not read as a save.
      const saveRoute = async () => {
        const result = edit
          ? await updateRoute(routeId, updatedValues)
          : await addRouteApi(updatedValues);
        if (result && result.error !== undefined) {
          const error = new Error(routeErrorText(result.error, t));
          error.apiError = result.error;
          throw error;
        }
        return result;
      };

      try {
        await toast.promise(
          saveRoute(),
          {
            pending: t("Guardando cambios..."),
            success: edit
              ? t("Ruta actualizada exitosamente")
              : t("Ruta creada exitosamente"),
            error: {
              render({ data }) {
                // `data.message` is backend text (already resolved by
                // routeErrorText); any other rejection never shows its message.
                return data?.apiError !== undefined
                  ? data.message
                  : t("Error al guardar la ruta");
              },
            },
          },
          {
            position: "bottom-center",
            autoClose: 2000,
          }
        );
        // Success only: refresh the list and close the modal.
        fetchRoutes()
          .catch(console.error)
          .finally(() => {
            setLoading(false);
            closeModal(false);
          });
      } catch (error) {
        console.log(error);
        setLoading(false); // the modal stays open to fix the data and retry
      }
    },
    [closeModal, edit, dayType, fetchRoutes, route, t]
  );

  const deliveryManValue = methods.watch("deliveryMan");
  const isFormComplete = !!deliveryManValue;

  const constructRouteName = useCallback(
    (values) => {
      const routeNumber = `${t("Ruta")} ${Math.floor(Math.random() * 1000)}`;
      let dayTypeText = t("Entre semana");
      if (dayType === "saturday") {
        dayTypeText = t("Sábado");
      } else if (dayType === "sunday") {
        dayTypeText = t("Domingo");
      }
      const delivererName = values.deliveryMan?.label || t("Sin repartidor");

      return `${routeNumber} - ${dayTypeText} - ${delivererName}`;
    },
    [dayType, t]
  );

  const handleDayTypeChange = (value) => {
    setDayType(value);
    methods.setValue("frequency", value);
  };

  const frequencyOptions = [
    { value: "weekday", label: t("Lunes a viernes"), icon: Calendar },
    { value: "saturday", label: t("Sábado"), icon: Calendar },
    { value: "sunday", label: t("Domingo"), icon: Calendar },
  ];

  if (!deliveries || deliveries.length === 0) {
    return (
      <div className="create-route-loading">
        <Spinner />
        <span>{t("Cargando repartidores...")}</span>
      </div>
    );
  }

  return (
    <div className="create-route">
      <div className="create-route__header">
        <div className="create-route__header-content">
          <RouteIcon size={24} className="create-route__header-icon" />
          <h2 className="create-route__title">
            {edit ? t("Editar ruta") : t("Crear nueva ruta")}
          </h2>
        </div>
        <button
          type="button"
          onClick={() => closeModal(false)}
          className="create-route__close-btn"
          aria-label={t("Cerrar")}
        >
          <X size={20} />
        </button>
      </div>

      <Form onSubmit={methods.handleSubmit(submitValues)} className="create-route__form">
        {/* Nombre de la ruta */}
        <div className="create-route__field">
          <label className="create-route__label">
            <RouteIcon size={16} />
            <span>{t("Nombre de la ruta")}</span>
          </label>
          <Controller
            name="routeName"
            control={methods.control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                className="create-route__input"
                placeholder={t("Ej: Ruta Centro - Lunes a viernes")}
                aria-label={t("Nombre de la ruta")}
              />
            )}
          />
          <span className="create-route__helper-text">
            {t("Si no ingresas un nombre, se generará automáticamente")}
          </span>
        </div>

        {/* Frecuencia */}
        <div className="create-route__field">
          <label className="create-route__label">
            <Calendar size={16} />
            <span>{t("Frecuencia de la ruta")}</span>
          </label>
          <div className="create-route__frequency-group">
            {frequencyOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = dayType === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleDayTypeChange(option.value)}
                  className={`create-route__frequency-option ${isSelected ? 'selected' : ''}`}
                  aria-pressed={isSelected}
                >
                  <Icon size={18} className="create-route__frequency-icon" />
                  <span>{option.label}</span>
                  {isSelected && (
                    <div className="create-route__frequency-check">
                      <Check size={16} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Repartidor */}
        <div className="create-route__field">
          <label className="create-route__label">
            <User size={16} />
            <span>{t("Repartidor asignado")}</span>
            <span className="create-route__required">*</span>
          </label>
          <div className="create-route__select-wrapper">
            <SearchDropdown
              options={deliveries.map((delivery) => ({
                value: delivery._id,
                label: `${delivery.name} ${delivery.lastName}`,
              }))}
              placeholder={t("Selecciona un repartidor")}
              selected={deliveryManValue ? (() => {
                const selectedDelivery = deliveries.find(d => d._id === deliveryManValue);
                return selectedDelivery ? `${selectedDelivery.name} ${selectedDelivery.lastName}` : "";
              })() : ""}
              onChange={(value) => {
                methods.setValue("deliveryMan", value);
              }}
              className="create-route__search-dropdown"
            />
          </div>
          {!isFormComplete && (
            <span className="create-route__error-text">
              {t("Debes seleccionar un repartidor")}
            </span>
          )}
        </div>

        {/* Botones de acción */}
        <div className="create-route__actions">
          <Button
            type="button"
            onClick={() => closeModal(false)}
            className="create-route__btn create-route__btn--cancel"
          >
            {t("Cancelar")}
          </Button>
          <Button
            type="submit"
            disabled={!isFormComplete || loading}
            className="create-route__btn create-route__btn--submit"
          >
            {loading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                {t("Guardando...")}
              </>
            ) : (
              <>
                <Check size={16} className="mr-2" />
                {edit ? t("Actualizar ruta") : t("Crear ruta")}
              </>
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
}
